import AssignmentTurnedIn from "@mui/icons-material/AssignmentTurnedIn";
import Business from "@mui/icons-material/Business";
import Contacts from "@mui/icons-material/Contacts";
import Delete from "@mui/icons-material/Delete";
import Description from "@mui/icons-material/Description";
import Home from "@mui/icons-material/Home";
import Map from "@mui/icons-material/Map";
import Send from "@mui/icons-material/Send";
import { SubmissionStepType } from "generated-graphql/graphql";
import React from "react";
import { ContactsStep } from "routes/Customer/Waste/Submissions/Stepper/steps/ContactsStep";
import { FacilityProfileStep } from "routes/Customer/Waste/Submissions/Stepper/steps/FacilityProfileStep";
import { ReviewStep } from "routes/Customer/Waste/Submissions/Stepper/steps/ReviewStep";
import { StateFieldsStep } from "routes/Customer/Waste/Submissions/Stepper/steps/StateFieldsStep";
import { VerifyAndSubmitStep } from "routes/Customer/Waste/Submissions/Stepper/steps/VerifyAndSubmitStep";
import { WasteActivityStep } from "routes/Customer/Waste/Submissions/Stepper/steps/WasteActivityStep";

export type SubmissionStepPresentation = {
  label: string;
  sourceDescription: string;
  /** Route segment the FE routes off; "" for the overview step. */
  routeSlug: string;
  icon: React.ReactNode;
  /**
   * Editor mounted by `StepRoute` for this step. Omitted for OVERVIEW, which is
   * the index route and renders `StepperOverview` directly (never via `StepRoute`).
   */
  Editor?: React.ComponentType<{ submissionId: string; facilityId: string }>;
};

export const WASTE_SUBMISSION_STEP_PRESENTATION: Record<
  SubmissionStepType,
  SubmissionStepPresentation
> = {
  [SubmissionStepType.Overview]: {
    label: "Overview",
    sourceDescription: "Review and validate the 8700-12 submission.",
    routeSlug: "",
    icon: <Home />,
  },
  [SubmissionStepType.SubmissionDetails]: {
    // Details is rendered inline at the top of the Overview (see `DetailsCard`),
    // not as a step in the nav — so it has no route/editor. The metadata entry
    // stays because the type requires every `SubmissionStepType`.
    label: "Details",
    sourceDescription: "Submission title, due date, and Encamp assignee.",
    routeSlug: "details",
    icon: <Description />,
  },
  [SubmissionStepType.FacilityProfile]: {
    label: "Facility Profile",
    sourceDescription: "Sourced from the facility's RCRA profile.",
    routeSlug: "facility-profile",
    icon: <Business />,
    Editor: FacilityProfileStep,
  },
  [SubmissionStepType.RcraWasteActivity]: {
    label: "Waste Activity",
    sourceDescription:
      "Federal & state generator status, waste streams, and reportable waste codes, all computed from manifest and vendor data.",
    routeSlug: "waste-activity",
    icon: <Delete />,
    Editor: WasteActivityStep,
  },
  [SubmissionStepType.Contacts]: {
    label: "Contacts",
    sourceDescription: "RCRA site contact, owner, operator, and additional contacts.",
    routeSlug: "contacts",
    icon: <Contacts />,
    Editor: ContactsStep,
  },
  [SubmissionStepType.StateFields]: {
    label: "State Info",
    sourceDescription: "State-specific RCRA fields for this facility's jurisdiction.",
    routeSlug: "state-info",
    icon: <Map />,
    Editor: StateFieldsStep,
  },
  [SubmissionStepType.Review]: {
    label: "Review",
    sourceDescription: "Reviewers sign off before submission.",
    routeSlug: "review",
    icon: <AssignmentTurnedIn />,
    Editor: ReviewStep,
  },
  [SubmissionStepType.VerifyAndSubmit]: {
    label: "Verify & Submit",
    sourceDescription: "Send to review / submit.",
    routeSlug: "verify-and-submit",
    icon: <Send />,
    Editor: VerifyAndSubmitStep,
  },
};
